<script lang="ts">
  import { DataTable, ChartBar, KpiCard, currencyPHP } from '@epitome-analytics/dashboard-kit';
  import { store } from '../lib/stores.svelte.js';
  import {
    fetchCityDiagnostic, fetchProvinceDiagnostic,
    type SkuInCity, type ProvinceDiagnostic,
  } from '../lib/queries.js';
  import { formatTier, fmtShare } from '../lib/format.js';

  /* Province drill — primary */
  let provData = $state<ProvinceDiagnostic | null>(null);
  /* City drill — secondary (sub-drill from province) */
  let cityRows = $state<SkuInCity[]>([]);
  let loading  = $state(false);
  let error    = $state<string | null>(null);

  $effect(() => {
    if (!store.period) { provData = null; cityRows = []; return; }

    if (store.selectedCity) {
      loading = true; error = null;
      fetchCityDiagnostic(store.period, store.selectedCity)
        .then((r) => { cityRows = r; loading = false; })
        .catch((e) => { error = (e as Error).message; loading = false; });
    } else if (store.selectedProvince) {
      loading = true; error = null;
      fetchProvinceDiagnostic(store.period, store.selectedProvince)
        .then((r) => { provData = r; loading = false; })
        .catch((e) => { error = (e as Error).message; loading = false; });
    } else {
      provData = null; cityRows = [];
    }
  });

  const skuColumns = [
    { key: 'brand' as const, label: 'Brand' },
    { key: 'sku' as const,   label: 'SKU' },
    {
      key: 'gpiv_tier' as const, label: 'Signal', align: 'center' as const,
      format: (_v: unknown, r: SkuInCity) => `<span style="color:${formatTier(r.gpiv_tier).color}">●</span> ${formatTier(r.gpiv_tier).label}`,
    },
    { key: 'gpiv' as const,   label: 'GPIv',   align: 'right' as const, format: (v: unknown) => Number(v).toFixed(2) },
    { key: 'sop' as const,    label: 'SoP',    align: 'right' as const, format: (v: unknown) => currencyPHP.formatPHPShort(Number(v)) },
    { key: 'my_gmv' as const, label: 'My GMV', align: 'right' as const, format: (v: unknown) => currencyPHP.formatPHPShort(Number(v)) },
    { key: 'share' as const,  label: 'Share',  align: 'right' as const, format: (v: unknown) => fmtShare(Number(v)) },
  ];

  const cityColumns = [
    { key: 'city' as const,    label: 'City' },
    { key: 'mp' as const,      label: 'MP',     align: 'center' as const, format: (v: unknown) => String(v) },
    { key: 'sop' as const,     label: 'SoP',    align: 'right' as const,  format: (v: unknown) => currencyPHP.formatPHPShort(Number(v)) },
    { key: 'my_gmv' as const,  label: 'My GMV', align: 'right' as const,  format: (v: unknown) => currencyPHP.formatPHPShort(Number(v)) },
    { key: 'cat_gmv' as const, label: 'Cat GMV',align: 'right' as const,  format: (v: unknown) => currencyPHP.formatPHPShort(Number(v)) },
    { key: 'share' as const,   label: 'Share',  align: 'right' as const,  format: (v: unknown) => fmtShare(Number(v)) },
  ];
</script>

{#if !store.selectedProvince && !store.selectedCity}
  <div class="empty">
    Click a province on the map to drill into it. Click a city for its heatmap reading.
  </div>

{:else if error}
  <div class="banner">{error}</div>

{:else if loading}
  <div class="banner muted">
    Loading {store.selectedCity ? `city #${store.selectedCity}` : store.selectedProvince}…
  </div>

{:else if store.selectedCity && cityRows.length > 0}
  <!-- ── CITY sub-drill ──────────────────────────────────────────── -->
  <section class="diag">
    <header>
      <span class="eyebrow">City Diagnostic</span>
      <h2>City #{store.selectedCity}</h2>
      <div class="header-actions">
        {#if store.selectedProvince}
          <button class="link" onclick={() => store.selectedCity = null}>
            ← back to {store.selectedProvince}
          </button>
        {/if}
        <button class="close" onclick={() => store.clearDrill()}>Close ×</button>
      </div>
    </header>

    <div class="grid">
      <div class="chart-block">
        <h3>Top 10 SKUs by Size of Prize</h3>
        <ChartBar
          categories={cityRows.slice(0, 10).map((r) => `${r.brand} · ${r.sku}`)}
          series={[{ name: 'SoP', data: cityRows.slice(0, 10).map((r) => r.sop) }]}
          orientation="horizontal"
          yFormatter={currencyPHP.formatPHPShort}
          height="380px"
          legend={false}
        />
      </div>
      <div class="table-block">
        <h3>All SKUs in this city ({cityRows.length})</h3>
        <DataTable rows={cityRows} columns={skuColumns} rowKey={(r) => String(r.sku_id)} />
      </div>
    </div>
  </section>

{:else if store.selectedProvince && provData}
  <!-- ── PROVINCE drill ─────────────────────────────────────────── -->
  <section class="diag">
    <header>
      <span class="eyebrow">Province Diagnostic</span>
      <h2>{store.selectedProvince}</h2>
      <button class="close" onclick={() => store.clearDrill()}>Close ×</button>
    </header>

    <section class="kpi-row">
      <KpiCard eyebrow="MY GMV"        label="8-week revenue"      value={currencyPHP.formatPHPShort(provData.totals.my_gmv)} />
      <KpiCard eyebrow="CAT GMV"       label="Category total"      value={currencyPHP.formatPHPShort(provData.totals.cat_gmv)} />
      <KpiCard eyebrow="SIZE OF PRIZE" label="Untapped revenue"    value={currencyPHP.formatPHPShort(provData.totals.sop)} />
      <KpiCard eyebrow="MY SHARE"      label="Of category"         value={fmtShare(provData.totals.share)} />
      <KpiCard eyebrow="COVERAGE"      label="SKU-city rows"       value={String(provData.totals.cities)} />
    </section>

    <div class="grid">
      <div class="table-block">
        <h3>Top cities in {store.selectedProvince} ({provData.topCities.length})</h3>
        <DataTable
          rows={provData.topCities}
          columns={cityColumns}
          rowKey={(r) => String(r.city_id)}
          onRowClick={(r) => store.selectCity(r.city_id)}
        />
      </div>
      <div class="table-block">
        <h3>Top SKUs in {store.selectedProvince}</h3>
        <DataTable
          rows={provData.topSkus}
          columns={skuColumns}
          rowKey={(r) => String(r.sku_id)}
        />
      </div>
    </div>
  </section>

{:else}
  <div class="banner muted">No data for the current filters in this scope.</div>
{/if}

<style>
  .empty, .banner {
    padding: var(--space-2xl);
    border-radius: var(--radius-md);
    text-align: center;
    background: var(--color-info-bg);
    color: var(--color-info);
    font-family: var(--font-ui);
  }
  .banner { background: var(--color-negative-bg); color: var(--color-negative); }
  .banner.muted { background: var(--color-info-bg); color: var(--color-info); }
  .diag { display: flex; flex-direction: column; gap: var(--space-lg); }
  header {
    display: flex;
    align-items: center;
    gap: var(--space-md);
  }
  header h2 {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--text-2xl);
    text-transform: uppercase;
    color: var(--color-navy);
  }
  .eyebrow {
    font-family: var(--font-ui);
    font-size: var(--text-xs);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-wider);
    text-transform: uppercase;
    color: var(--color-blue);
  }
  .header-actions {
    margin-left: auto;
    display: flex;
    gap: var(--space-sm);
    align-items: center;
  }
  .link {
    background: transparent;
    border: none;
    font-family: var(--font-ui);
    font-size: var(--text-xs);
    font-weight: var(--weight-semibold);
    color: var(--color-blue);
    cursor: pointer;
  }
  .link:hover { text-decoration: underline; }
  .close {
    font-family: var(--font-ui);
    font-size: var(--text-xs);
    font-weight: var(--weight-semibold);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wide);
    background: transparent;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    padding: 6px 12px;
    cursor: pointer;
    color: var(--color-text-muted);
  }
  .close:hover { color: var(--color-navy); border-color: var(--color-navy); }
  .kpi-row {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: var(--space-lg);
  }
  .grid {
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    gap: var(--space-lg);
  }
  .chart-block, .table-block {
    background: var(--color-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--space-lg);
    box-shadow: var(--shadow-card);
  }
  h3 {
    margin: 0 0 var(--space-md);
    font-family: var(--font-display);
    font-size: var(--text-md);
    text-transform: uppercase;
    color: var(--color-navy);
  }
</style>
